"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UploadManager = void 0;
const ttag_1 = require("ttag");
const interface_1 = require("../../interface");
const errors_1 = require("../../errors");
const nodes_1 = require("../nodes");
const uids_1 = require("../uids");
/**
 * UploadManager is responsible for creating and deleting draft nodes
 * on the server. It handles the creation of draft nodes, including
 * generating the necessary cryptographic keys and metadata.
 */
class UploadManager {
    apiService;
    cryptoService;
    nodesService;
    nodesEvents;
    logger;
    constructor(telemetry, apiService, cryptoService, nodesService, nodesEvents) {
        this.apiService = apiService;
        this.cryptoService = cryptoService;
        this.nodesService = nodesService;
        this.nodesEvents = nodesEvents;
        this.logger = telemetry.getLogger('upload');
        this.apiService = apiService;
        this.cryptoService = cryptoService;
        this.nodesService = nodesService;
    }
    async createDraftNode(parentFolderUid, name, metadata) {
        const parentKeys = await this.nodesService.getNodeKeys(parentFolderUid);
        if (!parentKeys.hashKey) {
            throw new errors_1.ValidationError((0, ttag_1.c)('Error').t `Creating files in non-folders is not allowed`);
        }
        const generatedNodeCrypto = await this.cryptoService.generateFileCrypto(parentFolderUid, { key: parentKeys.key, hashKey: parentKeys.hashKey }, name);
        const { nodeUid, nodeRevisionUid } = await this.createDraftOnAPI(parentFolderUid, parentKeys.hashKey, name, metadata, generatedNodeCrypto);
        return {
            nodeUid,
            nodeRevisionUid,
            nodeKeys: {
                key: generatedNodeCrypto.nodeKeys.decrypted.key,
                contentKeyPacketSessionKey: generatedNodeCrypto.contentKey.decrypted.contentKeyPacketSessionKey,
                signatureAddress: generatedNodeCrypto.signatureAddress,
            },
            newNodeInfo: {
                parentUid: parentFolderUid,
                name,
                encryptedName: generatedNodeCrypto.encryptedNode.encryptedName,
                hash: generatedNodeCrypto.encryptedNode.hash,
            },
        };
    }
    async createDraftOnAPI(parentFolderUid, parentHashKey, name, metadata, generatedNodeCrypto) {
        try {
            const result = await this.apiService.createDraft(parentFolderUid, {
                armoredEncryptedName: generatedNodeCrypto.encryptedNode.encryptedName,
                hash: generatedNodeCrypto.encryptedNode.hash,
                mediaType: metadata.mediaType,
                intendedUploadSize: metadata.expectedSize,
                armoredNodeKey: generatedNodeCrypto.nodeKeys.encrypted.armoredKey,
                armoredNodePassphrase: generatedNodeCrypto.nodeKeys.encrypted.armoredPassphrase,
                armoredNodePassphraseSignature: generatedNodeCrypto.nodeKeys.encrypted.armoredPassphraseSignature,
                base64ContentKeyPacket: generatedNodeCrypto.contentKey.encrypted.base64ContentKeyPacket,
                armoredContentKeyPacketSignature: generatedNodeCrypto.contentKey.encrypted.armoredContentKeyPacketSignature,
                signatureEmail: generatedNodeCrypto.signatureAddress.email,
                // FIXME: client UID
            });
            return result;
        }
        catch (error) {
            if (error instanceof errors_1.ValidationError) {
                if (error.code === 2500 /* ErrorCode.ALREADY_EXISTS */) {
                    this.logger.info(`Node with given name already exists`);
                    const typedDetails = error.details;
                    // If there is existing draft created by this client,
                    // automatically delete it and try to create a new one
                    // with the same name again.
                    if (typedDetails?.ConflictDraftRevisionID) {
                        const existingDraftNodeUid = (0, uids_1.makeNodeUid)((0, uids_1.splitNodeUid)(parentFolderUid).volumeId, typedDetails.ConflictLinkID);
                        let deleteFailed = false;
                        try {
                            this.logger.warn(`Deleting existing draft node ${existingDraftNodeUid}`);
                            await this.apiService.deleteDraft(existingDraftNodeUid);
                        }
                        catch (deleteDraftError) {
                            // Do not throw, let throw the conflict error.
                            deleteFailed = true;
                            this.logger.error('Failed to delete existing draft node', deleteDraftError);
                        }
                        if (!deleteFailed) {
                            return this.createDraftOnAPI(parentFolderUid, parentHashKey, name, metadata, generatedNodeCrypto);
                        }
                    }
                    const existingNodeUid = typedDetails ? (0, uids_1.makeNodeUid)((0, uids_1.splitNodeUid)(parentFolderUid).volumeId, typedDetails.ConflictLinkID) : undefined;
                    // If there is existing node, return special error
                    // that includes the available name the client can use.
                    throw new errors_1.NodeAlreadyExistsValidationError(error.message, error.code, existingNodeUid);
                }
            }
            throw error;
        }
    }
    async findAvailableName(parentFolderUid, name) {
        const { hashKey: parentHashKey } = await this.nodesService.getNodeKeys(parentFolderUid);
        if (!parentHashKey) {
            throw new errors_1.ValidationError((0, ttag_1.c)('Error').t `Creating files in non-folders is not allowed`);
        }
        const [namePart, extension] = splitExtension(name);
        const batchSize = 10;
        let startIndex = 1;
        while (true) {
            const namesToCheck = [];
            for (let i = startIndex; i < startIndex + batchSize; i++) {
                namesToCheck.push(joinNameAndExtension(namePart, i, extension));
            }
            const hashesToCheck = await this.cryptoService.generateNameHashes(parentHashKey, namesToCheck);
            const { availalbleHashes } = await this.apiService.checkAvailableHashes(parentFolderUid, hashesToCheck.map(({ hash }) => hash));
            if (!availalbleHashes.length) {
                startIndex += batchSize;
                continue;
            }
            const availableHash = hashesToCheck.find(({ hash }) => hash === availalbleHashes[0]);
            if (!availableHash) {
                throw Error('Backend returned unexpected hash');
            }
            return availableHash.name;
        }
    }
    async deleteDraftNode(nodeUid) {
        try {
            await this.apiService.deleteDraft(nodeUid);
        }
        catch (error) {
            // Only log the error but do not fail the operation as we are
            // deleting draft only when somethign fails and original error
            // will bubble up.
            this.logger.error('Failed to delete draft node', error);
        }
    }
    async createDraftRevision(nodeUid, metadata) {
        const node = await this.nodesService.getNode(nodeUid);
        const nodeKeys = await this.nodesService.getNodeKeys(nodeUid);
        if (!node.activeRevision?.ok || !nodeKeys.contentKeyPacketSessionKey) {
            throw new errors_1.ValidationError((0, ttag_1.c)('Error').t `Creating revisions in non-files is not allowed`);
        }
        const signatureAddress = await this.nodesService.getRootNodeEmailKey(nodeUid);
        const { nodeRevisionUid } = await this.apiService.createDraftRevision(nodeUid, {
            currentRevisionUid: node.activeRevision.value.uid,
            intendedUploadSize: metadata.expectedSize,
        });
        return {
            nodeUid,
            nodeRevisionUid,
            nodeKeys: {
                key: nodeKeys.key,
                contentKeyPacketSessionKey: nodeKeys.contentKeyPacketSessionKey,
                signatureAddress: signatureAddress,
            },
        };
    }
    async deleteDraftRevision(nodeRevisionUid) {
        try {
            await this.apiService.deleteDraftRevision(nodeRevisionUid);
        }
        catch (error) {
            // Only log the error but do not fail the operation as we are
            // deleting draft only when somethign fails and original error
            // will bubble up.
            this.logger.error('Failed to delete draft node revision', error);
        }
    }
    async commitDraft(nodeRevisionDraft, manifest, metadata, extendedAttributes, encryptedSize) {
        const generatedExtendedAttributes = (0, nodes_1.generateFileExtendedAttributes)(extendedAttributes);
        const nodeCommitCrypto = await this.cryptoService.commitFile(nodeRevisionDraft.nodeKeys, manifest, generatedExtendedAttributes);
        await this.apiService.commitDraftRevision(nodeRevisionDraft.nodeRevisionUid, nodeCommitCrypto);
        const activeRevision = (0, interface_1.resultOk)({
            uid: nodeRevisionDraft.nodeRevisionUid,
            state: interface_1.RevisionState.Active,
            creationTime: new Date(),
            storageSize: encryptedSize,
            contentAuthor: (0, interface_1.resultOk)(nodeCommitCrypto.signatureEmail),
            claimedSize: metadata.expectedSize,
            claimedModificationTime: extendedAttributes.modificationTime,
            claimedDigests: {
                sha1: extendedAttributes.digests?.sha1,
            },
        });
        if (nodeRevisionDraft.newNodeInfo) {
            const node = {
                // Internal metadata
                hash: nodeRevisionDraft.newNodeInfo.hash,
                encryptedName: nodeRevisionDraft.newNodeInfo.encryptedName,
                // Basic node metadata
                uid: nodeRevisionDraft.nodeUid,
                parentUid: nodeRevisionDraft.newNodeInfo.parentUid,
                type: interface_1.NodeType.File,
                mediaType: metadata.mediaType,
                creationTime: new Date(),
                totalStorageSize: encryptedSize,
                // Share node metadata
                isShared: false,
                directMemberRole: interface_1.MemberRole.Inherited,
                // Decrypted metadata
                isStale: false,
                keyAuthor: (0, interface_1.resultOk)(nodeRevisionDraft.nodeKeys.signatureAddress.email),
                nameAuthor: (0, interface_1.resultOk)(nodeRevisionDraft.nodeKeys.signatureAddress.email),
                name: (0, interface_1.resultOk)(nodeRevisionDraft.newNodeInfo.name),
                activeRevision,
            };
            await this.nodesEvents.nodeCreated(node);
        }
        else {
            await this.nodesEvents.nodeUpdated({
                uid: nodeRevisionDraft.nodeUid,
                activeRevision,
            });
        }
    }
}
exports.UploadManager = UploadManager;
/**
 * Split a filename into `[name, extension]`
 */
function splitExtension(filename = '') {
    const endIdx = filename.lastIndexOf('.');
    if (endIdx === -1 || endIdx === filename.length - 1) {
        return [filename, ''];
    }
    return [filename.slice(0, endIdx), filename.slice(endIdx + 1)];
}
;
/**
 * Join a filename into `name (index).extension`
 */
function joinNameAndExtension(name, index, extension) {
    if (!name && !extension) {
        return `(${index})`;
    }
    if (!name) {
        return `(${index}).${extension}`;
    }
    if (!extension) {
        return `${name} (${index})`;
    }
    return `${name} (${index}).${extension}`;
}
//# sourceMappingURL=manager.js.map